import { Input } from "@/components/ui/input"
import React, { forwardRef } from "react"

interface CurrencyInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: string | number;
  onChange: (value: string) => void;
  currency?: string;
  allowNegative?: boolean;
  helperText?: string;
  showExample?: boolean;
}

export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ value, onChange, currency = "USD", allowNegative = false, helperText, showExample = true, ...props }, ref) => {

    const formatCurrency = (val: string): string => {
      // Remover todo excepto números, punto y signo negativo
      let cleaned = val.replace(/[^\d.-]/g, '');

      // Si no permite negativos, remover el signo
      if (!allowNegative) {
        cleaned = cleaned.replace('-', '');
      }

      // Asegurar solo un punto decimal
      const parts = cleaned.split('.');
      if (parts.length > 2) {
        cleaned = parts[0] + '.' + parts.slice(1).join('');
      }

      // Limitar a 2 decimales
      if (parts[1] && parts[1].length > 2) {
        cleaned = parts[0] + '.' + parts[1].substring(0, 2);
      }

      return cleaned;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const formattedValue = formatCurrency(e.target.value);
      onChange(formattedValue);
    };

    const displayValue = value ? `${value}` : '';

    const currencySymbol = currency === "USD" ? "$" : currency;

    const defaultHelperText = "Ejemplo: 150.50 (los dos dígitos después del punto son centavos)";

    return (
      <div className="space-y-1">
        {(helperText || showExample) && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {helperText || defaultHelperText}
          </p>
        )}
        <div className="relative">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
          >
            {currencySymbol}
          </span>
          <Input
            ref={ref}
            type="text"
            inputMode="decimal"
            value={displayValue}
            onChange={handleChange}
            {...props}
            className={`pl-6 ${props.className || ''}`}
          />
        </div>
      </div>
    );
  }
);

CurrencyInput.displayName = "CurrencyInput";

// Utilidad para convertir a número (para Appwrite)
export const parseCurrencyToFloat = (value: string | number): number => {
  if (typeof value === 'number') return value;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

// Demo del componente con ejemplos de uso
export default function CurrencyInputDemo() {
  // Estado como STRING (recomendado para UX)
  const [estimatedCost, setEstimatedCost] = React.useState("");
  const [travelBudget, setTravelBudget] = React.useState("1500.50");

  // Estado como NUMBER (si prefieres)
  const [dailyAllowance, setDailyAllowance] = React.useState<number>(0);

  const handleSubmit = () => {
    // Ejemplo de cómo enviar a Appwrite
    const dataToAppwrite = {
      estimatedCost: parseCurrencyToFloat(estimatedCost), // string -> float
      travelBudget: parseCurrencyToFloat(travelBudget),   // string -> float
      dailyAllowance: dailyAllowance                       // ya es number
    };

    console.log('Datos para Appwrite:', dataToAppwrite);
    alert(`
      Costo estimado: $${dataToAppwrite.estimatedCost}
      Presupuesto: $${dataToAppwrite.travelBudget}
      Viáticos: $${dataToAppwrite.dailyAllowance}
    `);
  };

  return (
    <div className="w-full max-w-md mx-auto p-6 space-y-6">
      <h2 className="text-2xl font-bold mb-4">Currency Input para Appwrite</h2>

      <div className="space-y-2">
        <label className="text-sm font-medium">
          Costo Estimado (con texto por defecto):
        </label>
        <CurrencyInput
          value={estimatedCost}
          onChange={setEstimatedCost}
          placeholder="0.00"
        />
        <p className="text-xs text-gray-500">
          Estado actual: "{estimatedCost}" |
          Para Appwrite: {parseCurrencyToFloat(estimatedCost)}
        </p>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">
          Presupuesto (con texto personalizado):
        </label>
        <CurrencyInput
          value={travelBudget}
          onChange={setTravelBudget}
          placeholder="0.00"
          helperText="Ingresa el monto total del viaje. Ej: 1500.00"
        />
        <p className="text-xs text-gray-500">
          Estado actual: "{travelBudget}" |
          Para Appwrite: {parseCurrencyToFloat(travelBudget)}
        </p>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">
          Viáticos (sin texto de ayuda):
        </label>
        <CurrencyInput
          value={dailyAllowance}
          onChange={(val) => setDailyAllowance(parseCurrencyToFloat(val))}
          placeholder="0.00"
          showExample={false}
        />
        <p className="text-xs text-gray-500">
          Estado actual: {dailyAllowance} (tipo: number)
        </p>
      </div>

      <button
        onClick={handleSubmit}
        className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
      >
        Ver valores para Appwrite
      </button>

      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
        <h3 className="font-semibold mb-2 text-green-800">💡 Recomendación:</h3>
        <div className="text-sm space-y-2 text-gray-700">
          <p><strong>Estado como string:</strong></p>
          <code className="block bg-white p-2 rounded text-xs">
            const [cost, setCost] = useState("");
          </code>
          <p className="mt-2"><strong>Al guardar en Appwrite:</strong></p>
          <code className="block bg-white p-2 rounded text-xs">
            estimatedCost: parseFloat(cost) || 0
          </code>
        </div>
      </div>

      <div className="p-4 bg-blue-50 rounded-lg">
        <h3 className="font-semibold mb-2">Opciones del componente:</h3>
        <ul className="text-sm space-y-1 text-gray-700">
          <li>✓ Texto de ayuda automático por defecto</li>
          <li>✓ Puedes personalizar con <code>helperText=""</code></li>
          <li>✓ Desactivar con <code>showExample={"{false}"}</code></li>
          <li>✓ El texto aparece encima del input</li>
        </ul>
      </div>
    </div>
  );
}